<!--
  <SmoothReflow class="overflow-hidden transition-all duration-200 ease-in-out">
    <div :class="{ 'h-auto': isOpen, 'h-0': !isOpen }">
      Hello World!
    </div>
  </SmoothReflow>
-->

<template>
  <component :is="tag" ref="root">
    <slot />
  </component>
</template>

<script setup lang="ts">
// CSS can't transition to/from `height: auto`, which is what a collapsed/expanded child normally toggles
// between (`h-0`/`h-auto` and similar). This observes the wrapper's rendered size and, on every change
// (however it was caused - a class toggle, a v-if, a v-for growing/shrinking), briefly pins an explicit
// pixel height across the change so the `transition-all` the caller applies has two concrete values to
// animate between, then releases back to `height: auto` once the transition finishes.
defineProps({
  tag: { type: String, default: 'div' },
})

const root = ref<HTMLElement>()
let resizeObserver: ResizeObserver | undefined
let isAnimating = false
let lastHeight = 0

function onTransitionEnd(event: TransitionEvent) {
  if (event.target !== root.value || event.propertyName !== 'height') return
  isAnimating = false
  root.value!.style.height = ''
}

onMounted(() => {
  const el = root.value
  if (!el) return

  lastHeight = el.getBoundingClientRect().height
  el.addEventListener('transitionend', onTransitionEnd)

  resizeObserver = new ResizeObserver((entries) => {
    if (isAnimating || !root.value) return
    // contentRect is the wrapper's own layout box (like clientHeight), not scrollHeight - the latter also
    // counts a child's unclipped overflow (e.g. a fixed-height child with the default `overflow: visible`
    // whose own content spills past it), which reports roughly the same "full" height whether the wrapper
    // is collapsed or expanded and so animates only the last sliver of the real change.
    const newHeight = entries[0]?.contentRect.height ?? 0
    if (Math.round(newHeight) === Math.round(lastHeight)) return

    isAnimating = true
    const startHeight = lastHeight
    lastHeight = newHeight
    root.value.style.height = `${startHeight}px`
    // The browser only animates a property change between two rendered frames, so the new height must be
    // set on a later frame, not just after a forced layout in the same task - otherwise both writes land
    // in the same paint and there is nothing to transition from.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (root.value) root.value.style.height = `${newHeight}px`
      })
    })
  })
  resizeObserver.observe(el)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  root.value?.removeEventListener('transitionend', onTransitionEnd)
})
</script>
